import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Self-contained Cloudflare Turnstile widget — loads CF's script directly (no npm
// dependency) and renders a Managed widget. Calls onToken(token) when solved and
// onToken('') when it expires/errors. Falls back to Cloudflare's documented
// always-pass TEST sitekey when REACT_APP_TURNSTILE_SITE_KEY is unset, so local
// dev never gets blocked. The server still verifies the token (lib/turnstile.js).
//
// Tokens are SINGLE-USE: once the server has seen one (even on a 4xx like a
// duplicate email), retrying the same token fails. Parents must hold a ref and
// call reset() from their mutation onError so the next submit carries a fresh
// token.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TEST_SITE_KEY = '1x00000000000000000000AA'; // always-passes, localhost-safe

const ensureScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (!window.__turnstileScriptPromise) {
    window.__turnstileScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = resolve;
      s.onerror = (e) => {
        // Don't cache the rejection — clear the promise (and the dead <script>)
        // so a later attempt can try the load again instead of failing forever.
        window.__turnstileScriptPromise = null;
        s.remove();
        reject(e);
      };
      document.head.appendChild(s);
    });
  }
  return window.__turnstileScriptPromise;
};

const Turnstile = forwardRef(({ onToken }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep the latest callback without re-rendering the widget on every parent render.
  const cbRef = useRef(onToken);
  cbRef.current = onToken;
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the Retry button to re-run the load/render effect.
  const [loadAttempt, setLoadAttempt] = useState(0);

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || TEST_SITE_KEY;

  useImperativeHandle(ref, () => ({
    // Clear the (spent) token and ask Cloudflare for a fresh challenge. Call
    // after any server rejection — see the single-use note above.
    reset: () => {
      cbRef.current(null);
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (e) {
          /* widget already gone */
        }
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'dark', // matches the dark referral/partner forms (no white box)
          // Invisible for normal visitors — runs silently and only shows a widget
          // if Cloudflare decides an interactive challenge is actually needed.
          appearance: 'interaction-only',
          // A token after an error (Cloudflare retries on its own) clears
          // the Retry line (QA 2026-10-15).
          callback: (token) => {
            cbRef.current(token);
            if (!cancelled) setLoadFailed(false);
          },
          'expired-callback': () => cbRef.current(''),
          // An error (wrong hostname for the site key, a privacy blocker,
          // Cloudflare trouble) leaves an invisible widget and no token: the
          // form said "complete the security check below" with nothing there
          // (2026-10-14). Show the Retry under it; the widget stays mounted so
          // Cloudflare's own retry, or an interactive challenge, still has a
          // place to render (QA 2026-10-15).
          'error-callback': () => {
            cbRef.current('');
            if (!cancelled) setLoadFailed(true);
          },
        });
      })
      .catch(() => {
        // Script failed to load (blocked/offline) — surface a retry affordance
        // instead of leaving the form silently unsubmittable.
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, loadAttempt]);

  return (
    <>
      <div ref={containerRef} className="flex justify-center" />
      {loadFailed ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => {
              setLoadFailed(false);
              setLoadAttempt((n) => n + 1);
            }}
            className="text-xs text-neutral-400 underline underline-offset-2 hover:text-white"
          >
            Security check didn&apos;t load. Retry
          </button>
        </div>
      ) : null}
    </>
  );
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
